import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Checkbox, DriftSnackbar } from 'drift-ui';
import cx from 'classnames';
import { DO_APPLICATION_TYPE } from 'client-experience-toolbox';
import ContinueButton from 'components/ContinueButton';
import PdfStateDocumentViewer from 'components/PdfStateDocumentViewer';
import { useApplication } from 'hooks/useApplication';
import { AgreementsMetadata } from 'services/digitalOnboardingService';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';
import baseStyles from './BaseFormStyles.scss';
import styles from './EsignAgreement.scss';

export const EsignAgreement = () => {
  const dispatch = useDispatch();
  const { application, section } = useApplication(DO_APPLICATION_TYPE.BANKING);
  const [agreeChecked, setAgreeChecked] = useState(
    Boolean(application?.sectionsCompletion?.esignAgreement?.completedAt),
  );
  const [error, setError] = useState(false);
  const [snackbar, setSnackbar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
  const [agreementMetadata, setAgreementMetadata] = useState<AgreementsMetadata | null>(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(false);
  const { setSectionComplete, getAgreements, updateAgreementMetadata } = useFormNetworkCalls(
    DO_APPLICATION_TYPE.BANKING,
  );
  const { t } = useTranslation();

  const fetchAgreementPdf = useCallback(async () => {
    try {
      setPdfLoading(true);
      setPdfError(false);
      const { pdfData, metadata } = await getAgreements(DO_APPLICATION_TYPE.BANKING, 'esignAgreement');
      setAgreementMetadata(metadata);
      setPdfData(pdfData);
      setNumPages(null);
    } catch {
      setPdfError(true);
    } finally {
      setPdfLoading(false);
    }
  }, [getAgreements]);

  const onContinue = async () => {
    if (!agreeChecked) {
      setError(true);
      return;
    }
    try {
      setIsLoading(true);
      await updateAgreementMetadata('esignAgreement', agreementMetadata ?? null);
      const sectionsCompletion = {
        esignAgreement: { completedAt: Date.now() },
      };
      await setSectionComplete(sectionsCompletion);
      dispatch({ type: 'SET_BANKING_SECTION', bankingSection: section + 1 });
    } catch {
      setSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAgreementPdf();
  }, [fetchAgreementPdf]);

  return (
    <div className={cx(baseStyles.formContainer, styles.esignFormContainer)}>
      <div className={styles.container}>
        <p className={cx(styles.descriptionText, pdfLoading && styles.noRightPadding)}>
          {t('digital_onboarding.banking.esign_agreement_page.description')}
        </p>
        <PdfStateDocumentViewer
          fileData={pdfData}
          isLoading={pdfLoading}
          hasError={pdfError}
          numPages={numPages}
          onRefresh={fetchAgreementPdf}
          onLoadSuccess={(loadedPages) => {
            setNumPages(loadedPages);
            setPdfLoading(false);
          }}
          onLoadError={(pdfLoadError) => {
            console.error('Error loading PDF:', pdfLoadError);
            setPdfLoading(false);
            setPdfError(true);
          }}
          pageKeyPrefix="page"
        />

        {!pdfError && !pdfLoading && (
          <>
            <div className={cx(styles.checkboxContainer, error && styles.errorCheckboxContainer)}>
              <p className={styles.checkboxTitle}>
                {t('digital_onboarding.banking.esign_agreement_page.by_checking_box')}
              </p>
              {error && (
                <p className={styles.errorText}>
                  {t('digital_onboarding.banking.esign_agreement_page.check_box_to_continue')}
                </p>
              )}
              <div>
                <label className={styles.checkbox}>
                  <Checkbox
                    checked={agreeChecked}
                    slotProps={{
                      input: {
                        'data-nid-target': 'esign-agreement-checkbox',
                      } as React.InputHTMLAttributes<HTMLInputElement>,
                    }}
                    onChange={() => {
                      setError(false);
                      setAgreeChecked((prev) => !prev);
                    }}
                  />
                  <p className={styles.checkboxText}>
                    {t('digital_onboarding.banking.esign_agreement_page.agree_to_esign')}
                  </p>
                </label>
              </div>
            </div>
            <div className={styles.buttonContainer}>
              <ContinueButton
                title={t('digital_onboarding.common_section.agree_and_continue')}
                buttonStyles={styles.buttonStyles}
                handleContinueClick={onContinue}
                isPatching={isLoading}
              />
            </div>
          </>
        )}
      </div>
      <DriftSnackbar
        open={snackbar}
        variant="error"
        message={t('please_try_again')}
        autoHideDuration={3000}
        onClose={(prev) => setSnackbar(prev)}
      />
    </div>
  );
};
